import { css } from "@emotion/css";
import { Chip, ListTitle } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ChannelResource, DeploymentActionResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import type * as React from "react";
import type { Lifecycles, ParentEnvironments } from "~/areas/projects/components/Channels/ChannelsPage";
import { DefaultOptionChip } from "~/components/Chips";
import MarkdownDescription from "~/components/MarkdownDescription";
import TenantTagsList from "~/components/TenantTagsList/TenantTagsList";
import type { TagIndex } from "~/components/tagSets";
import { EphemeralEnvironmentChannelInfoPopover, LifecycleChannelInfoPopover } from "../EphemeralEnvironments/EphemeralEnvironmentsInfoPopovers";
import { useEphemeralEnvironmentsEnabled } from "../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { GitReferenceRuleDescription } from "./GitProtectionRules/GitReferenceRuleDescription";
import { GitResourceRulesDescriptionList } from "./GitProtectionRules/GitResourceRulesDescriptionList";
import RulesDescriptionList from "./RulesDescriptionList";
interface ChannelProps {
    channel: ChannelResource;
    project: ProjectResource;
    lifecycles: Lifecycles;
    parentEnvironments: ParentEnvironments;
    tagIndex: TagIndex;
    deploymentActions?: DeploymentActionResource[];
}
const Channel: React.FC<ChannelProps> = (props) => {
    const ephemeralEnvironmentsEnabledForProject = useEphemeralEnvironmentsEnabled();
    const getLifecycleOrParentEnvironmentName = () => {
        if (props.channel.Type === "EphemeralEnvironment") {
            if (!props.channel.ParentEnvironmentId) {
                throw new Error("Ephemeral Environment channel is missing ParentEnvironmentId");
            }
            const parent = props.parentEnvironments[props.channel.ParentEnvironmentId];
            return parent?.Name ?? props.channel.ParentEnvironmentId;
        }
        if (props.channel.LifecycleId) {
            return props.lifecycles[props.channel.LifecycleId].Name;
        }
        const projectLifecycle = props.lifecycles[props.project.LifecycleId].Name;
        return `${projectLifecycle} (inherited from project)`;
    };
    const channel = props.channel;
    return (<div>
            <ListTitle>
                <div className={channelStyles.title}>
                    <div className={channelStyles.label}>{channel.Name}</div>
                    <div>
                        {channel.IsDefault && <DefaultOptionChip />}
                        {channel.Type === "EphemeralEnvironment" && <Chip backgroundColor={themeTokens.color.chip.filled.background.info} label="Ephemeral Environments" labelColor={themeTokens.color.chip.filled.text.info}/>}
                    </div>
                </div>
            </ListTitle>
            <div className={channelStyles.description}>
                <MarkdownDescription markup={channel.Description}/>
            </div>
            <div className={channelStyles.grid}>
                <div className={channelStyles.row}>
                    <div className={`${channelStyles.header} ${channelStyles.infoButton}`}>
                        {channel.Type === "EphemeralEnvironment" ? "Parent Environment" : "Lifecycle"}
                        {ephemeralEnvironmentsEnabledForProject && (channel.Type === "EphemeralEnvironment" ? <EphemeralEnvironmentChannelInfoPopover /> : <LifecycleChannelInfoPopover />)}
                    </div>
                    <div>{getLifecycleOrParentEnvironmentName()}</div>
                </div>
                {channel.TenantTags.length > 0 && props.project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted && (<div className={channelStyles.row}>
                        <div className={channelStyles.header}>Limited to these tenants</div>
                        <div>
                            <TenantTagsList tags={channel.TenantTags}/>
                        </div>
                    </div>)}
                {channel.Rules.length > 0 && (<div className={channelStyles.row}>
                        <div className={channelStyles.header}>Package version rules</div>
                        <div>
                            <RulesDescriptionList rules={channel.Rules}/>
                        </div>
                    </div>)}
                {channel.GitReferenceRules.length > 0 && (<div className={channelStyles.row}>
                        <div className={channelStyles.header}>Project repository</div>
                        <div>
                            <GitReferenceRuleDescription rules={channel.GitReferenceRules}/>
                        </div>
                    </div>)}
                {channel.GitResourceRules.length > 0 && (<div className={channelStyles.row}>
                        <div className={channelStyles.header}>External repository rules</div>
                        <div>
                            <GitResourceRulesDescriptionList rules={channel.GitResourceRules} actions={props.deploymentActions}/>
                        </div>
                    </div>)}
            </div>
        </div>);
};
Channel.displayName = "Channel"
const channelStyles = {
    title: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    }),
    description: css({
        marginBottom: space[4],
    }),
    label: css({
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        justifyContent: "center",
        alignItems: "center",
    }),
    infoButton: css({ display: "flex", flexDirection: "row", gap: 8 }),
    grid: css({
        display: "flex",
        flexDirection: "column",
    }),
    row: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: space[8],
        margin: `${space[8]} 0`,
        "@media (min-width: 768px)": {
            margin: `${space[4]} 0`,
            gap: space[24],
            flexWrap: "nowrap",
        },
    }),
    header: css({
        flex: "0 0 auto",
        alignItems: "center",
        font: text.body.bold.medium,
    }),
};
export default Channel;
